import { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, Cpu, FolderGit2, Laptop, Save, Sliders } from "lucide-react";
import { Header } from "../components/Header.tsx";
import { api, errorText, type User } from "../lib/api.ts";
import { resetLayouts } from "../lib/useIdeLayout.ts";
import { DEFAULT_LAYOUT, saveLayoutModel } from "../lib/layoutModel.ts";
import { loadEditorSettings, saveEditorSettings, type EditorSettings } from "../lib/editorSettings.ts";
import { setAutocompleteStatus } from "../lib/autocomplete/autocompleteLogic.ts";
import { IMPLEMENTED_SHORTCUTS } from "../lib/keyboardShortcuts.ts";
import { navigate } from "../lib/router.ts";
import type { SettingsSection } from "../lib/routes.ts";
import { ProvidersSection } from "./settings/ProvidersSection.tsx";
import { UsageSection } from "./settings/UsageSection.tsx";
import { MachinesSection } from "./settings/MachinesSection.tsx";
import { ProjectsSection } from "./settings/ProjectsSection.tsx";

export interface DirtyHandler {
  isDirty: boolean;
  save: () => Promise<void> | void;
  discard?: () => void;
}

const SECTIONS: Array<{
  id: SettingsSection;
  label: string;
  icon: typeof Sliders;
  title: string;
  subtitle: string;
}> = [
  {
    id: "general",
    label: "General",
    icon: Sliders,
    title: "General",
    subtitle: "Manage your account, passkeys, code formatting preferences, and layout.",
  },
  {
    id: "providers",
    label: "Providers & Models",
    icon: Cpu,
    title: "Providers & Models",
    subtitle: "Configure AI providers, encrypted machine credentials, and default model roles.",
  },
  {
    id: "usage",
    label: "Usage & Limits",
    icon: BarChart3,
    title: "Usage & Limits",
    subtitle: "Inspect token consumption, estimated costs, and configure spending safety ceilings.",
  },
  {
    id: "machines",
    label: "Machines",
    icon: Laptop,
    title: "Machines",
    subtitle: "Manage paired development machines, runtime presence, and revoke credentials.",
  },
  {
    id: "projects",
    label: "Projects",
    icon: FolderGit2,
    title: "Projects",
    subtitle: "Manage registered projects, repositories, and local folders.",
  },
];

/** Settings: a left list of sections and the selected section. The section is part of the URL. */
export function SettingsPage({
  user,
  section,
  onLogout,
}: {
  user: User;
  section: SettingsSection;
  onLogout: () => void;
}) {
  const [dirtyHandler, setDirtyHandler] = useState<DirtyHandler | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const activeSection = useMemo(() => {
    return SECTIONS.find((s) => s.id === section) ?? SECTIONS[0]!;
  }, [section]);

  useEffect(() => {
    setDirtyHandler(null);
    setSaveSuccess(false);
    setSaveError(null);
  }, [section]);

  const handleSave = async () => {
    if (!dirtyHandler || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await dirtyHandler.save();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (err) {
      setSaveError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (dirtyHandler?.discard) {
      dirtyHandler.discard();
    }
  };

  return (
    <div className="page">
      <Header user={user} onLogout={onLogout} context={<span className="crumb">Settings</span>} />
      <div className="settings">
        <nav className="settings-nav" aria-label="Settings sections">
          <div className="settings-nav-title">Settings</div>
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = section === s.id;
            return (
              <button
                key={s.id}
                className={isActive ? "active" : ""}
                onClick={() => navigate(`/settings/${s.id}`)}
                data-testid={`settings-${s.id}`}
              >
                <Icon size={15} className="settings-nav-icon" aria-hidden />
                <span>{s.label}</span>
              </button>
            );
          })}
        </nav>
        <main className="settings-body" data-testid="settings-body">
          <header className="settings-header">
            <div className="settings-header-content">
              <h1 className="settings-header-title">{activeSection.title}</h1>
              <p className="settings-header-subtitle">{activeSection.subtitle}</p>
            </div>
            <div className="settings-header-actions">
              {dirtyHandler?.isDirty && (
                <button
                  type="button"
                  className="settings-discard-btn"
                  onClick={handleDiscard}
                  disabled={saving}
                >
                  Discard
                </button>
              )}
              {(section === "general" || section === "providers" || section === "usage") && (
                <button
                  type="button"
                  className="primary settings-save-btn"
                  onClick={() => {
                    void handleSave();
                  }}
                  disabled={!dirtyHandler?.isDirty || saving}
                  data-testid="settings-save-btn"
                >
                  <Save size={13} aria-hidden />
                  {saving ? "Saving…" : saveSuccess ? "Saved!" : "Save Changes"}
                </button>
              )}
            </div>
          </header>

          {saveError && <p className="error small" style={{ marginBottom: 16 }}>{saveError}</p>}

          {section === "general" && <General user={user} onDirtyChange={setDirtyHandler} />}
          {section === "providers" && <ProvidersSection onDirtyChange={setDirtyHandler} />}
          {section === "usage" && <UsageSection onDirtyChange={setDirtyHandler} />}
          {section === "machines" && <MachinesSection />}
          {(section === "projects" || (section as string) === "workspaces") && <ProjectsSection />}
        </main>
      </div>
    </div>
  );
}

function General({
  user,
  onDirtyChange,
}: {
  user: User;
  onDirtyChange?: (handler: DirtyHandler | null) => void;
}) {
  const [status, setStatus] = useState<string | null>(null);
  const [layoutReset, setLayoutReset] = useState(false);
  const [savedSettings, setSavedSettings] = useState<EditorSettings>(() => loadEditorSettings());
  const [pendingSettings, setPendingSettings] = useState<EditorSettings>(() => loadEditorSettings());

  useEffect(() => setLayoutReset(false), []);

  const isDirty = useMemo(() => {
    return (
      savedSettings.formatOnSave !== pendingSettings.formatOnSave ||
      savedSettings.formatOnPaste !== pendingSettings.formatOnPaste ||
      savedSettings.autocomplete !== pendingSettings.autocomplete
    );
  }, [savedSettings, pendingSettings]);

  const saveSettings = useCallback(async () => {
    saveEditorSettings(pendingSettings);
    if (pendingSettings.autocomplete !== savedSettings.autocomplete) {
      setAutocompleteStatus({ enabled: pendingSettings.autocomplete });
    }
    setSavedSettings(pendingSettings);
  }, [pendingSettings, savedSettings]);

  const discardSettings = useCallback(() => {
    setPendingSettings(savedSettings);
  }, [savedSettings]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange({
        isDirty,
        save: saveSettings,
        discard: discardSettings,
      });
    }
  }, [isDirty, onDirtyChange, saveSettings, discardSettings]);

  const updatePending = (patch: Partial<EditorSettings>) => {
    setPendingSettings((prev) => ({ ...prev, ...patch }));
  };

  return (
    <>
      <section className="settings-card set-block">
        <div className="settings-card-header">
          <div>
            <h3 className="settings-card-title">Account</h3>
            <p className="settings-card-desc">
              Signed in as <strong>{user.displayName}</strong>.
            </p>
          </div>
        </div>
        <div>
          <button
            className="action-btn"
            onClick={() =>
              void api
                .addPasskey()
                .then(() => setStatus("Passkey added successfully."))
                .catch((e: unknown) => setStatus(errorText(e)))
            }
            data-testid="add-passkey"
          >
            Add another passkey
          </button>
          {status && <p className="muted small" style={{ marginTop: 8 }}>{status}</p>}
        </div>
      </section>

      <section className="settings-card set-block">
        <div className="settings-card-header">
          <div>
            <h3 className="settings-card-title">Formatting &amp; Editing</h3>
            <p className="settings-card-desc">
              Smart auto-indentation is enabled by default. Formatting uses your project&apos;s Prettier or Biome configuration.
            </p>
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <span className="settings-row-label">Format on Save</span>
            <span className="settings-row-desc">Automatically format document files upon saving.</span>
          </div>
          <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={pendingSettings.formatOnSave}
              onChange={(e) => updatePending({ formatOnSave: e.target.checked })}
              data-testid="format-on-save-checkbox"
            />
          </label>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <span className="settings-row-label">Format on Paste</span>
            <span className="settings-row-desc">Format pasted code snippets to match indentation and style.</span>
          </div>
          <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={pendingSettings.formatOnPaste}
              onChange={(e) => updatePending({ formatOnPaste: e.target.checked })}
              data-testid="format-on-paste-checkbox"
            />
          </label>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <span className="settings-row-label">AI Autocomplete</span>
            <span className="settings-row-desc">Display inline ghost text completions as you type in Monaco.</span>
          </div>
          <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={pendingSettings.autocomplete}
              onChange={(e) => updatePending({ autocomplete: e.target.checked })}
              data-testid="autocomplete-checkbox"
            />
          </label>
        </div>
      </section>
      <section className="settings-card set-block">
        <div className="settings-card-header">
          <div>
            <h3 className="settings-card-title">Appearance</h3>
            <p className="settings-card-desc">
              Interface theme and display settings.
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <div
            style={{
              padding: "10px 16px",
              borderRadius: 6,
              border: "1px solid var(--accent)",
              background: "var(--accent-muted)",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            Dark (Active)
          </div>
          <div
            style={{
              padding: "10px 16px",
              borderRadius: 6,
              border: "1px solid var(--border)",
              color: "var(--muted)",
              fontSize: 13,
              opacity: 0.6,
            }}
          >
            Light (Planned)
          </div>
        </div>
      </section>

      <section className="settings-card set-block">
        <div className="settings-card-header">
          <div>
            <h3 className="settings-card-title">Layout</h3>
            <p className="settings-card-desc">
              Panel visibility, sizes, docking and the History panel are remembered in this browser. Reset
              restores the default layout: Files | Code + Tools | Agent, with History inside the Agent.
            </p>
          </div>
        </div>
        <div>
          <button
            className="action-btn"
            onClick={() => {
              resetLayouts();
              saveLayoutModel(localStorage, DEFAULT_LAYOUT);
              setLayoutReset(true);
            }}
            data-testid="reset-layout"
          >
            Reset layout
          </button>
          {layoutReset && (
            <p className="muted small" style={{ marginTop: 8 }}>
              Layout reset. It applies the next time you open a Project.
            </p>
          )}
        </div>
      </section>

      <section className="settings-card set-block">
        <div className="settings-card-header">
          <div>
            <h3 className="settings-card-title">Keyboard Shortcuts</h3>
            <p className="settings-card-desc">Standard keyboard shortcuts for editor navigation and actions.</p>
          </div>
        </div>
        <table className="kv">
          <tbody>
            {IMPLEMENTED_SHORTCUTS.map((s) => (
              <tr key={s.key}>
                <td style={{ width: 220 }}>
                  <span className="settings-kbd">{s.key}</span>
                </td>
                <td className="muted small">{s.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}
